import { FilterDropdown } from "@/components/Filter/FilterDropdown";
import { applyFilters, useFilter } from "@/components/Filter/useFilter";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { CreateGroupModal } from "@/components/group/CreateGroupModal";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/input";
import { TableSkeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGlobalGroupsData } from "@/data/use-global-groups-data";
import { useFilterBySearchText } from "@/hooks/use-filter-by-search-text";
import { GetGroupDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMemo, useState } from "react";

export default function GlobalAdminGroupsPage() {
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  return (
    <>
      <AppTopRightMenu.ContentPortal display="append">
        <Button onClick={() => setIsCreatingGroup(true)}>
          <FontAwesomeIcon icon={faPlus} />
          Create Group
        </Button>
      </AppTopRightMenu.ContentPortal>

      <Page>
        <PageHeader>
          <PageHeader.Title>Global Groups Management</PageHeader.Title>
        </PageHeader>
        <PageBody>
          <h1 className="text-2xl font-bold">Groups</h1>
          <Contents />
        </PageBody>
      </Page>

      <CreateGroupModal
        open={isCreatingGroup}
        onOpenChange={setIsCreatingGroup}
      />
    </>
  );
}

const Contents = () => {
  const { groups, isLoading } = useGlobalGroupsData();
  const { searchText, setSearchText, filteredItems } = useFilterBySearchText(
    groups,
    ["name", "slug", "salthubClientId"],
  );
  const {
    selected: selectedOrganization,
    setSelected: setSelectedOrganization,
    options: organizationUuids,
  } = useFilter(groups, "organizationUuid");
  const organizationNames = useMemo(
    () =>
      new Map(
        groups.map((group) => [group.organizationUuid, group.organizationName]),
      ),
    [groups],
  );
  const getOrganizationLabel = (organizationUuid: string | null) =>
    organizationUuid
      ? (organizationNames.get(organizationUuid) ?? organizationUuid)
      : "All Organizations";
  const organizationOptions = [...organizationUuids].sort((left, right) =>
    getOrganizationLabel(left).localeCompare(getOrganizationLabel(right)),
  );
  const visibleGroups = applyFilters(filteredItems, {
    organizationUuid: selectedOrganization,
  });

  if (isLoading) {
    return <TableSkeleton />;
  }

  return (
    <>
      <div className="flex h-12 items-center gap-2 px-2">
        <SearchInput
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search..."
          aria-label="Search groups"
        />
        <FilterDropdown
          label="Organization"
          options={organizationOptions}
          value={selectedOrganization}
          onChange={setSelectedOrganization}
          getLabel={getOrganizationLabel}
          allLabel="All Organizations"
          className="border-border"
        />
      </div>
      {visibleGroups.length === 0 ? (
        <div className="text-muted-foreground py-4 text-center text-sm">
          No data available
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-accent text-xs font-semibold tracking-wide capitalize">
                Name
              </TableHead>
              <TableHead className="text-accent text-xs font-semibold tracking-wide capitalize">
                Slug
              </TableHead>
              <TableHead className="text-accent text-xs font-semibold tracking-wide capitalize">
                Organization
              </TableHead>
              <TableHead className="text-accent text-xs font-semibold tracking-wide capitalize">
                Client ID
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleGroups.map((group) => (
              <GroupLinkRow key={group.uuid} group={group} />
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

const GroupLinkRow = ({ group }: { group: GetGroupDto }) => (
  <TableRow
    link={{
      to: "/admin/groups/$groupUuid",
      params: { groupUuid: group.uuid },
    }}
  >
    <TableCell>{group.name}</TableCell>
    <TableCell>{group.slug}</TableCell>
    <TableCell>{group.organizationName}</TableCell>
    <TableCell>
      {group.salthubClientId ?? <span className="text-gray-400">None</span>}
    </TableCell>
  </TableRow>
);
